import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import {
  Modal,
  Dropdown,
  Input,
  Icon,
  Dimmer,
  Loader,
  Header,
} from "semantic-ui-react";
import { useToasts } from "react-toast-notifications";
import {
  SG_EDIT_EMPLOYEE_CATEGORY,
  SG_ADD_EMPLOYEE_CATEGORY,
  SG_UPDATE_EMPLOYEE_AUDIT,
  SG_ADD_EMPLOYEE_PERMISSION,
  SG_EDIT_EMPLOYEE_PERMISSION,
  SG_GET_EMPLOYEE_ORG_CATEGORIES,
  SG_GET_PROD_ORG_CONTRACT,
  PULL_EMPLOYEE,
} from "constants/actions";
import { LockIcon, SaveIcon, XIcon, CategoryIcon, SearchIcon, TrashIcon } from "./Icons";
import Select from "react-select";

const DETAILED_PERMISSIONS = [
  {
    permissionLabel: "Can Access Recommendations",
    permissionDescription:
      "Member will have access to the recommendation reports.",
    permissionValue: "access_recommendations",
  },
];



const AddPermissionModal = ({ isOpen, onClose, onSave, editingEmployee }) => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  
  // State for employee search and selection
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // State for permissions
  const [selectedPrimaryCategories, setSelectedPrimaryCategories] = useState([]);
  const [selectedDetailedPermissions, setSelectedDetailedPermissions] = useState([]);
  const [currentDetailedPermission, setCurrentDetailedPermission] = useState(null);
  const [allSurveyOptions, setAllSurveyOptions] = useState([]);
  
  // Redux state
  const {
    get_employee,
    get_survey_structure,
    get_employee_category,
    get_auth,
    get_employee_permission,
  } = useSelector(
    (state) => ({
      get_employee: state.employees,
      get_survey_structure: state.surveystructure.survey_structure,
      get_employee_category: state.employee_category.employee_org_category,
      get_auth: state.auth,
      get_employee_permission: state.employee_permission,
    }),
    shallowEqual
  );

  // Derived data
  const [teamOptions, setTeamOptions] = useState([]);
  const [primaryApiOptions, setPrimaryApiOptions] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Initialize current user and admin status
  useEffect(() => {
    if (get_employee?.userEmp) {
      setCurrentUser(get_employee.userEmp);
      setIsAdmin(get_employee.userEmp.account_type === 5);
    }
  }, [get_employee]);

  // Handle editing mode - pre-populate form when editingEmployee is provided
  useEffect(() => {
    if (editingEmployee && isOpen) {
      setSelectedEmployee(editingEmployee);
      setSearchTerm(editingEmployee.email);
      
      // Load existing data
      const employeeData = get_employee?.[editingEmployee.id];
      if (employeeData) {
        // Load existing categories
        const existingCategories = get_employee_category?.find(
          (c) => c.employee_id === editingEmployee.id
        );
        
        if (existingCategories?.category) {
          const primaryCats = existingCategories.category
            .filter(c => c.priority === "primary")
            .map(c => c.value_id);
          setSelectedPrimaryCategories(primaryCats);
        }

        // Load existing detailed permissions
        const existingPermission = get_employee_permission?.employee_permission?.find(
          (p) => p.employee_id === editingEmployee.id
        );
        
        if (existingPermission?.details) {
          setSelectedDetailedPermissions(existingPermission.details);
        }
      }
    }
  }, [editingEmployee, isOpen, get_employee, get_employee_category, get_employee_permission]);

  // Load survey structure on mount
  useEffect(() => {
    dispatch({
      type: SG_GET_PROD_ORG_CONTRACT,
    });
  }, [dispatch]);

  // Filter employees based on search term
  useEffect(() => {
    if (get_employee?.employees) {
      const filtered = get_employee.employees
        .filter(emp => 
          emp.status === 1 && // Only active employees
          (emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           emp.email?.toLowerCase().includes(searchTerm.toLowerCase()))
        )
        .slice(0, 20); // Limit results for performance
      
      setFilteredEmployees(filtered);
    }
  }, [get_employee?.employees, searchTerm]);

  // Set up team options from survey structure
  useEffect(() => {
    if (get_survey_structure && get_survey_structure.length > 0) {
      const primary = get_survey_structure[0]?.categories?.find(
        (c) => c.priority === "primary"
      );

      if (primary && primary.options) {
        const options = primary.options.map(cat => ({
          key: cat.name,
          text: cat.name,
          value: cat.id,
        }));

        const apiOptions = primary.options.map(cat => ({
          name: primary.name,
          name_id: primary.id,
          value: cat.name,
          priority: "primary",
          value_id: cat.id,
          id: cat.id,
        }));

        setTeamOptions(options);
        setPrimaryApiOptions(apiOptions);
      }
    }
  }, [get_survey_structure]);

  // Build detailed permission options from survey structure
  useEffect(() => {
    const _optionList = [];
    
    // Add primary options first
    primaryApiOptions.map((po) => {
      return _optionList.push({
        name_id: po?.name_id,
        name: po?.value,
        value_id: po?.value_id,
        value: `Primary - ${po?.value}`,
        text: "Primary",
      });
    });

    if (currentUser?.account_type === 5) {
      // Admin user - can see all categories
      if (get_survey_structure?.[0]?.depth === 1) {
        get_survey_structure?.[0]?.categories
          .filter(
            (ca) => ca.priority !== "primary" && ca.visibility === "public"
          )
          .map((ss) => {
            ss?.options.map((op) =>
              _optionList.push({
                name_id: ss?.id,
                name: ss?.name,
                value_id: op?.id,
                value: `${ss.name} - ${op.name}`,
                text: op.name,
              })
            );
            return null;
          });
      }
      // Handle subfactors where depth === 2
      if (get_survey_structure?.[0]?.depth === 2) {
        get_survey_structure?.[0]?.categories
          .filter(
            (ca) => ca.priority !== "primary" && ca.visibility === "public"
          )
          .forEach((category) => {
            category?.subfactors?.forEach((ss) => {
              ss?.options.forEach((op) =>
                _optionList.push({
                  name_id: ss?.id,
                  name: ss?.name,
                  value_id: op?.id,
                  value: `${ss.name} - ${op.name}`,
                  text: op.name,
                })
              );
            });
          });
      }
    } else {
      // Non-admin user - only see categories they have access to
      const _validList = currentUser?.employee_employee_category?.category
        .filter((ca) => ca.priority !== "primary")
        .map((cc) => cc.value_id);

      if (get_survey_structure?.[0]?.depth === 1) {
        get_survey_structure?.[0]?.categories
          .filter(
            (ca) => ca.priority !== "primary" && ca.visibility === "public"
          )
          .map((ss) => {
            ss?.options.map((op) => {
              if (_validList?.includes(op?.id)) {
                _optionList.push({
                  name_id: ss?.id,
                  name: ss?.name,
                  value_id: op?.id,
                  value: `${ss.name} - ${op.name}`,
                  text: op.name,
                });
              }
              return null;
            });
            return null;
          });
      }
      // Handle subfactors where depth === 2
      if (get_survey_structure?.[0]?.depth === 2) {
        get_survey_structure?.[0]?.categories
          .filter(
            (ca) => ca.priority !== "primary" && ca.visibility === "public"
          )
          .forEach((category) => {
            category?.subfactors?.forEach((ss) => {
              ss?.options.forEach((op) => {
                if (_validList?.includes(op?.id)) {
                  _optionList.push({
                    name_id: ss?.id,
                    name: ss?.name,
                    value_id: op?.id,
                    value: `${ss.name} - ${op.name}`,
                    text: op.name,
                  });
                }
              });
            });
          });
      }
    }

    setAllSurveyOptions(_optionList);
  }, [get_survey_structure, currentUser, primaryApiOptions]);

  // Load employee details when selected
  useEffect(() => {
    if (selectedEmployee && !get_employee?.[selectedEmployee.id]) {
      setLoading(true);
      dispatch({
        type: PULL_EMPLOYEE,
        payload: { id: selectedEmployee.id },
      });
    }
  }, [selectedEmployee, dispatch, get_employee]);

  // Handle employee selection
  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    
    // Reset form state
    setSelectedPrimaryCategories([]);
    setSelectedDetailedPermissions([]);
    
    // Load existing permissions if any
    const employeeData = get_employee?.[employee.id];
    if (employeeData) {
      
      // Load existing categories
      const existingCategories = get_employee_category?.find(
        (c) => c.employee_id === employee.id
      );
      
      if (existingCategories?.category) {
        const primaryCats = existingCategories.category
          .filter(c => c.priority === "primary")
          .map(c => c.value_id);
        setSelectedPrimaryCategories(primaryCats);
      }

      // Load existing detailed permissions
      const existingPermission = get_employee_permission?.employee_permission?.find(
        (p) => p.employee_id === employee.id
      );
      
      if (existingPermission?.details) {
        setSelectedDetailedPermissions(existingPermission.details);
      }
    }
    
    setLoading(false);
  };



  // Handle primary category selection
  const handlePrimaryCategorySelection = (value) => {
    setSelectedPrimaryCategories(value);
  };

  // Handle detailed permission selection from dropdown
  const handleDetailedPermissionChange = (selectedOption) => {
    setCurrentDetailedPermission(selectedOption);
  };

  // Add detailed permission
  const handleAddDetailedPermission = () => {
    if (currentDetailedPermission) {
      const newPermission = {
        name_id: currentDetailedPermission.name_id,
        name: currentDetailedPermission.name,
        value_id: currentDetailedPermission.value_id,
        value: currentDetailedPermission.value,
        text: currentDetailedPermission.text,
      };

      // Check if permission already exists
      const exists = selectedDetailedPermissions.some(
        perm => perm.value_id === newPermission.value_id
      );

      if (!exists) {
        setSelectedDetailedPermissions([...selectedDetailedPermissions, newPermission]);
        setCurrentDetailedPermission(null);
        addToast("Permission added", { appearance: "success", autoDismiss: true });
      } else {
        addToast("Permission already exists", { appearance: "warning", autoDismiss: true });
      }
    }
  };

  // Remove detailed permission
  const handleRemoveDetailedPermission = (permissionToRemove) => {
    const updatedPermissions = selectedDetailedPermissions.filter(
      perm => perm.value_id !== permissionToRemove.value_id
    );
    setSelectedDetailedPermissions(updatedPermissions);
    addToast("Permission removed", { appearance: "info", autoDismiss: true });
  };

  // Generate primary category data for API
  const getPrimaryCategoryData = () => {
    return selectedPrimaryCategories.map(categoryId => {
      const apiOption = primaryApiOptions.find(option => option.value_id === categoryId);
      return {
        name: apiOption?.name,
        name_id: apiOption?.name_id,
        value: apiOption?.value,
        value_id: categoryId,
        priority: "primary",
      };
    });
  };

  // Handle save
  const handleSave = async () => {
    if (!selectedEmployee) {
      addToast("Please select an employee", { appearance: "error", autoDismiss: true });
      return;
    }

    if (selectedPrimaryCategories.length === 0) {
      addToast("Please select at least one primary category", { 
        appearance: "error", 
        autoDismiss: true 
      });
      return;
    }

    setLoading(true);

    try {
      // Update basic permissions (login, data analytics) - always true when granting
      const auditObject = {
        id: selectedEmployee.id,
        enable_login: true,
        access_data_analytics: 1,
        access_subfactor_data: 1, // Usually linked
      };

      dispatch({
        type: SG_UPDATE_EMPLOYEE_AUDIT,
        payload: auditObject,
      });

      // Handle primary categories
      if (selectedPrimaryCategories.length > 0) {
        const primaryCategoryData = getPrimaryCategoryData();
        const existingCategory = get_employee_category?.find(
          (c) => c.employee_id === selectedEmployee.id
        );

        if (existingCategory) {
          dispatch({
            type: SG_EDIT_EMPLOYEE_CATEGORY,
            payload: {
              id: existingCategory.id,
              category: primaryCategoryData,
            },
          });
        } else {
          dispatch({
            type: SG_ADD_EMPLOYEE_CATEGORY,
            payload: {
              employee: selectedEmployee.id,
              category: primaryCategoryData,
            },
          });
        }
      }

      // Handle detailed permissions
      if (selectedDetailedPermissions.length > 0) {
        const existingPermission = get_employee_permission?.employee_permission?.find(
          (p) => p.employee_id === selectedEmployee.id
        );

        if (existingPermission) {
          dispatch({
            type: SG_EDIT_EMPLOYEE_PERMISSION,
            payload: {
              body: {
                id: existingPermission.id,
                details: selectedDetailedPermissions,
              },
              query: `organization=${get_auth?.organization_id}`,
            },
          });
        } else {
          dispatch({
            type: SG_ADD_EMPLOYEE_PERMISSION,
            payload: {
              body: {
                employee: selectedEmployee.id,
                details: selectedDetailedPermissions,
              },
              query: `organization=${get_auth?.organization_id}`,
            },
          });
        }
      }

      addToast(
        editingEmployee 
          ? "Permissions updated successfully!" 
          : "Permissions added successfully!", 
        { appearance: "success", autoDismiss: true }
      );
      
      // Call parent callback
      if (onSave) {
        onSave(selectedEmployee, {
          hasLogin: true,
          accessDatalytics: true,
          primaryCategories: selectedPrimaryCategories,
          detailedPermissions: selectedDetailedPermissions,
        });
      }
      
      handleClose();
    } catch (error) {
      addToast("Error updating permissions", { appearance: "error", autoDismiss: true });
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setSelectedEmployee(null);
    setSearchTerm("");
    setSelectedPrimaryCategories([]);
    setSelectedDetailedPermissions([]);
    setCurrentDetailedPermission(null);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="large"
      closeIcon
    >
      <ModalHeader>
        <HeaderTitle>
          {editingEmployee ? 'Edit Employee Permissions' : 'Add Employee Permissions'}
        </HeaderTitle>
      </ModalHeader>
      
      <ModalContent>
        <Dimmer.Dimmable blurring dimmed={loading}>
          {/* Employee Search Section */}
          {!editingEmployee ? (
            <Section>
              <SectionHeader>
                <IconWrapper>{SearchIcon()}</IconWrapper>
                <SectionTitle>Select Employee</SectionTitle>
              </SectionHeader>
              
              <SearchContainer>
                <Input
                  icon="search"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fluid
                />
              </SearchContainer>
              
              {searchTerm && (
                <EmployeeList>
                  {filteredEmployees.map((employee) => (
                    <EmployeeItem
                      key={employee.id}
                      selected={selectedEmployee?.id === employee.id}
                      onClick={() => handleEmployeeSelect(employee)}
                    >
                      <EmployeeName>
                        {employee.first_name} {employee.last_name}
                      </EmployeeName>
                      <EmployeeEmail>{employee.email}</EmployeeEmail>
                    </EmployeeItem>
                  ))}
                  {filteredEmployees.length === 0 && (
                    <NoResults>No employees found</NoResults>
                  )}
                </EmployeeList>
              )}
            </Section>
          ) : (
            <Section>
              <SectionHeader>
                <IconWrapper>{SearchIcon()}</IconWrapper>
                <SectionTitle>Editing Permissions For:</SectionTitle>
              </SectionHeader>
              <SelectedEmployeeCard>
                <EmployeeName>
                  {editingEmployee.first_name} {editingEmployee.last_name}
                </EmployeeName>
                <EmployeeEmail>{editingEmployee.email}</EmployeeEmail>
              </SelectedEmployeeCard>
            </Section>
          )}

          {selectedEmployee && (
            <>
              {/* Primary Categories */}
              <Section>
                <SectionHeader>
                  <IconWrapper>{CategoryIcon()}</IconWrapper>
                  <SectionTitle>Grant primary access to:</SectionTitle>
                </SectionHeader>
                
                <CategoryContainer>
                  <StyledDropdownWrapper>
                    <Dropdown
                      placeholder="Select primary categories..."
                      fluid
                      multiple
                      selection
                      search
                      options={teamOptions}
                      value={selectedPrimaryCategories}
                      onChange={(e, { value }) => handlePrimaryCategorySelection(value)}
                    />
                  </StyledDropdownWrapper>
                </CategoryContainer>
              </Section>

              {/* Detailed Permissions */}
              <Section>
                <SectionHeader>
                  <IconWrapper>{LockIcon()}</IconWrapper>
                  <SectionTitle>Detailed Permissions</SectionTitle>
                </SectionHeader>
                <DetailedPermissionDescription>
                  Assign granular permissions based on any category. When a category is assigned to a user, they will only ever be able to see data and users based on that category.
                </DetailedPermissionDescription>

                {/* Current Detailed Permissions Table */}
                {selectedDetailedPermissions.length > 0 && (
                  <PermissionsTable>
                    <PermissionsTableHeader>
                      <PermissionsTableHeading>Category</PermissionsTableHeading>
                      <PermissionsTableHeading>Action</PermissionsTableHeading>
                    </PermissionsTableHeader>
                    <PermissionsTableBody>
                      {selectedDetailedPermissions.map((permission, index) => (
                        <PermissionsTableRow key={index}>
                          <PermissionsTableCell>{permission.value}</PermissionsTableCell>
                          <PermissionsTableCell>
                            <RemovePermissionButton
                              onClick={() => handleRemoveDetailedPermission(permission)}
                            >
                              {TrashIcon()}
                            </RemovePermissionButton>
                          </PermissionsTableCell>
                        </PermissionsTableRow>
                      ))}
                    </PermissionsTableBody>
                  </PermissionsTable>
                )}

                {/* Add New Detailed Permission */}
                <AddPermissionContainer>
                  <PermissionSelectContainer>
                    <Select
                      placeholder="Select a category permission..."
                      onChange={handleDetailedPermissionChange}
                      value={currentDetailedPermission}
                      options={allSurveyOptions}
                      getOptionLabel={(option) => option?.value}
                      getOptionValue={(option) => option?.value_id}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          minHeight: '40px',
                          fontFamily: 'Poppins',
                        }),
                      }}
                    />
                  </PermissionSelectContainer>
                  <AddPermissionButton 
                    onClick={handleAddDetailedPermission}
                    disabled={!currentDetailedPermission}
                  >
                    <SaveIconWrapper>{SaveIcon()}</SaveIconWrapper>
                    <SaveText>Add Permission</SaveText>
                  </AddPermissionButton>
                </AddPermissionContainer>
              </Section>
            </>
          )}

          <Dimmer active={loading}>
            <Loader />
            <Header as="h2" icon inverted>
              Updating permissions...
            </Header>
          </Dimmer>
        </Dimmer.Dimmable>
      </ModalContent>

      <ModalActions>
        <CancelButton onClick={handleClose}>
          <CancelButtonText>Cancel</CancelButtonText>
          <CancelButtonIcon>{XIcon()}</CancelButtonIcon>
        </CancelButton>
        
        <SaveButton 
          onClick={handleSave}
          disabled={!selectedEmployee || loading}
        >
          <SaveIconWrapper>{SaveIcon()}</SaveIconWrapper>
          <SaveText>
            {editingEmployee ? 'Update Permissions' : 'Save Permissions'}
          </SaveText>
        </SaveButton>
      </ModalActions>
    </Modal>
  );
};

export default AddPermissionModal;

// Styled Components
const ModalHeader = styled.div`
  padding: 24px 32px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const HeaderTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 24px;
  color: #414141;
  margin: 0;
`;

const ModalContent = styled.div`
  padding: 32px;
  max-height: 70vh;
  overflow-y: auto;
`;

const ModalActions = styled.div`
  padding: 24px 32px;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0;
  margin-left: 8px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const EmployeeList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
`;

const EmployeeItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  background: ${props => props.selected ? '#e3f2fd' : 'white'};
  
  &:hover {
    background: ${props => props.selected ? '#e3f2fd' : '#f5f5f5'};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const EmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

const EmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const NoResults = styled.div`
  padding: 16px;
  text-align: center;
  color: #7e7e7e;
  font-family: "Poppins";
`;

const SelectedEmployeeCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;



const CategoryContainer = styled.div`
  max-width: 500px;
`;



const CancelButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  cursor: pointer;
`;

const CancelButtonText = styled.span`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #c0c0c0;
`;

const CancelButtonIcon = styled.div`
  display: flex;
  align-items: center;
`;

const SaveButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: ${props => props.disabled ? '#cccccc' : '#476dfa'};
  border-radius: 24px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

const SaveIconWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const SaveText = styled.span`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: white;
`;

const DetailedPermissionDescription = styled.div`
  font-family: "Poppins";
  font-style: normal;
  font-weight: 300;
  font-size: 14px;
  line-height: 24px;
  display: flex;
  align-items: center;
  max-width: 800px;
  color: #7e7e7e;
  margin-bottom: 20px;
`;

const PermissionsTable = styled.div`
  width: 100%;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
`;

const PermissionsTableHeader = styled.div`
  display: flex;
  background: #f8f9fa;
  height: 51px;
  padding-bottom: 5px;
  flex-direction: row;
  border-radius: 10px 10px 0 0;
`;

const PermissionsTableHeading = styled.div`
  width: 75%;
  padding-left: 15px;
  text-align: inherit;
  align-self: center;
  font-family: "Poppins";
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #414141;
  
  &:last-child {
    width: 25%;
    text-align: center;
  }
`;

const PermissionsTableBody = styled.div`
  width: 100%;
`;

const PermissionsTableRow = styled.div`
  height: 60px;
  display: flex;
  background: #ffffff;
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.05);
  margin-top: 5px;
  order: 0;
  flex-direction: row;
  flex-grow: 0;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: rgba(0, 0, 0, 0.87);
  }
`;

const PermissionsTableCell = styled.div`
  width: 75%;
  padding-left: 15px;
  text-align: inherit;
  align-self: center;
  font-family: "Poppins";
  font-style: normal;
  flex-direction: row;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #7e7e7e;
  
  &:last-child {
    width: 25%;
    text-align: center;
    padding-left: 0;
  }
`;

const RemovePermissionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #dc3545;
  padding: 4px 8px;
  border-radius: 4px;
  
  &:hover {
    background: #ffeaea;
  }
`;

const AddPermissionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 15px;
  padding-top: 15px;
  padding-bottom: 5px;
`;

const PermissionSelectContainer = styled.div`
  flex: 1;
  max-width: 400px;
`;

const AddPermissionButton = styled.div`
  padding: 8px 20px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  background: ${props => props.disabled ? '#cccccc' : '#476dfa'};
  border-radius: 100px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  opacity: ${props => props.disabled ? 0.6 : 1};
  min-width: 150px;
`;

const StyledDropdownWrapper = styled.div`
  .ui.dropdown {
    background: white !important;
    border: 1px solid #e0e0e0 !important;
    border-radius: 6px !important;
    font-family: "Poppins" !important;
  }
  
  .ui.dropdown > .text {
    color: #333 !important;
  }
  
  .ui.dropdown > .default.text {
    color: #999 !important;
  }
  
  .ui.dropdown .menu {
    background: white !important;
    border: 1px solid #e0e0e0 !important;
    border-radius: 6px !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
  }
  
  .ui.dropdown .menu > .item {
    background: white !important;
    color: #333 !important;
    font-family: "Poppins" !important;
    padding: 10px 16px !important;
  }
  
  .ui.dropdown .menu > .item:hover {
    background: #f5f5f5 !important;
  }
  
  .ui.dropdown .menu > .item.selected {
    background: #e3f2fd !important;
    color: #1976d2 !important;
  }
  
  .ui.multiple.dropdown > .label {
    background: #e3f2fd !important;
    color: #1976d2 !important;
    border-radius: 4px !important;
    font-family: "Poppins" !important;
    font-size: 12px !important;
  }
  
  .ui.multiple.dropdown > .label .delete.icon {
    color: #1976d2 !important;
  }
`; 